import { createColumnHelper } from '@tanstack/react-table';

import type { SurchargeDTO } from '@invenco/shipping-interface';

import { Badge } from '@/components/core/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/core/popover';
import { StatusBadge, StatusType } from '@/components/status-badge';
import { formatDate, formatMoney } from '@/shared/helpers';

import { describeApplicability } from './surcharge-applicability';

export type SurchargeStatus = 'ACTIVE' | 'SCHEDULED' | 'SUPERSEDED';

/** A surcharge with the UI-only derivations the table renders. */
export type SurchargeRow = SurchargeDTO & {
  /** "All services" when carrier-scoped, otherwise the service name (or code/id fallback). */
  appliesTo: string;
  status: SurchargeStatus;
  /** Whether other versions exist in the same name+scope group (informs the delete copy). */
  hasOtherVersions: boolean;
};

/** Versions of the same surcharge share a name and a scope (carrier-wide XOR one service); the NUL escape keeps the key collision-free against names containing any printable delimiter. */
export const surchargeGroupKey = (surcharge: SurchargeDTO) =>
  `${surcharge.name}\u0000${surcharge.carrierServiceId ?? ''}`;

/**
 * Resolves each row's lifecycle status within its name+scope group: `SCHEDULED` when its
 * validFrom is still in the future, `ACTIVE` for the latest validFrom that has taken effect,
 * and `SUPERSEDED` for every older version (a newer row with the same name+scope replaced it).
 */
export function computeSurchargeStatuses(
  surcharges: SurchargeDTO[],
  now = new Date(),
): Map<string, SurchargeStatus> {
  const statuses = new Map<string, SurchargeStatus>();
  const activeByGroup = new Map<string, SurchargeDTO>();

  surcharges.forEach((surcharge) => {
    if (new Date(surcharge.validFrom) > now) {
      statuses.set(surcharge.id, 'SCHEDULED');
      return;
    }
    const key = surchargeGroupKey(surcharge);
    const current = activeByGroup.get(key);
    if (!current || new Date(surcharge.validFrom) > new Date(current.validFrom)) {
      if (current) statuses.set(current.id, 'SUPERSEDED');
      activeByGroup.set(key, surcharge);
      statuses.set(surcharge.id, 'ACTIVE');
    } else {
      statuses.set(surcharge.id, 'SUPERSEDED');
    }
  });

  return statuses;
}

export const surchargeChargeMethodTitle: Record<SurchargeDTO['chargeMethod'], string> = {
  PERCENTAGE: 'Percentage',
  FIXED: 'Fixed',
};

const surchargeStatusTitle: Record<SurchargeStatus, string> = {
  ACTIVE: 'Active',
  SCHEDULED: 'Scheduled',
  SUPERSEDED: 'Superseded',
};

const surchargeStatusType: Record<SurchargeStatus, StatusType> = {
  ACTIVE: 'success',
  SCHEDULED: 'progress',
  SUPERSEDED: 'inactive',
};

/** `19.5%` for PERCENTAGE (fraction × 100, trailing zeros trimmed), `$75.00` for FIXED. */
export function formatSurchargeRate({
  chargeMethod,
  rate,
}: Pick<SurchargeDTO, 'chargeMethod' | 'rate'>): string {
  if (chargeMethod === 'FIXED') return formatMoney(rate);
  // toFixed bounds the float noise of rate × 100 (0.195 → 19.499999…); Number() trims the zeros.
  return `${Number((rate * 100).toFixed(6))}%`;
}

function ConditionsCell({ applicability }: Pick<SurchargeDTO, 'applicability'>) {
  // Empty cell — the DataTable renders its own muted no-value dash via peer-empty.
  if (!applicability) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="cursor-pointer" aria-label="View conditions">
          <Badge variant="outline">Conditional</Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto max-w-md">
        <p className="max-h-64 overflow-auto text-sm">{describeApplicability(applicability)}</p>
      </PopoverContent>
    </Popover>
  );
}

const columnHelper = createColumnHelper<SurchargeRow>();

export const surchargeColumns = [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor('appliesTo', {
    header: 'Applies to',
  }),
  columnHelper.accessor('chargeMethod', {
    header: 'Method',
    cell: ({ getValue }) => surchargeChargeMethodTitle[getValue()],
  }),
  columnHelper.accessor('rate', {
    header: 'Rate',
    cell: ({ row }) => formatSurchargeRate(row.original),
  }),
  columnHelper.accessor('applicability', {
    header: 'Conditions',
    enableSorting: false,
    cell: ({ getValue }) => <ConditionsCell applicability={getValue()} />,
  }),
  columnHelper.accessor('validFrom', {
    header: 'Effective from',
    cell: ({ getValue }) => formatDate(getValue()),
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => (
      <StatusBadge
        status={getValue()}
        type={surchargeStatusType[getValue()]}
        title={surchargeStatusTitle[getValue()]}
      />
    ),
  }),
];
